"use client";

/**
 * Rail empty state with book icon, explanation text, and sample citation chips.
 *
 * Extracted from `compass-intake.jsx:705-711` (`.cs-empty` class).
 */

import { Book } from "../lib/icons";

export interface EmptyStateProps {
  /** Heading text. */
  title?: string;
  /** Explanatory body text. */
  description?: string;
}

/**
 * Rail empty state — shows a centered book icon ring, heading, body text,
 * and a row of decorative citation chips.
 */
export function EmptyState({
  title = "Sources appear here",
  description = "Continue intake chat and Sources will appear here as they become available.",
}: EmptyStateProps) {
  return (
    <div
      className={[
        "h-full flex flex-col items-center justify-center",
        "text-center px-9 py-10",
        "text-(--fg-muted)",
      ].join(" ")}
    >
      {/* Icon ring */}
      <div
        className={[
          "w-[54px] h-[54px]",
          "rounded-(--radius-container)",
          "bg-(--bg-canvas)",
          "flex items-center justify-center",
          "text-(--fg-subtle) mb-4",
        ].join(" ")}
      >
        <Book size={24} />
      </div>

      <h4 className="m-0 mb-[7px] text-[15px] font-semibold text-(--fg-secondary)">
        {title}
      </h4>
      <p className="m-0 text-[13px] leading-[1.5] max-w-[240px]">
        {description}
      </p>

      {/* Decorative citation chips */}
      <div className="flex gap-1.5 mt-[18px] flex-wrap justify-center">
        {[1, 2, 3, 4, 5].map((n) => (
          <span
            key={n}
            className={[
              "w-5 h-5",
              "rounded-(--radius-xs)",
              "bg-(--bg-sky) border border-(--border-default)",
              "text-(--fg-brand) text-[11px] font-bold",
              "flex items-center justify-center",
            ].join(" ")}
          >
            {n}
          </span>
        ))}
      </div>
    </div>
  );
}
